import { toBase58Id } from "../util/base58";
import { snap } from "./blit";

/**
 * Crisp 3×5 bitmap font for entity labels — fillRect only, sharp when upscaled.
 * Covers Arch base58 alphabet + hex + '#'.
 */

type Glyph = number[];

const TINY_W = 3;
const TINY_H = 5;

/** 3×5 glyphs — 5 rows of 3 bits. */
const TINY: Record<string, Glyph> = {
  "1": [0b010, 0b110, 0b010, 0b010, 0b111],
  "2": [0b111, 0b001, 0b111, 0b100, 0b111],
  "3": [0b111, 0b001, 0b111, 0b001, 0b111],
  "4": [0b101, 0b101, 0b111, 0b001, 0b001],
  "5": [0b111, 0b100, 0b111, 0b001, 0b111],
  "6": [0b111, 0b100, 0b111, 0b101, 0b111],
  "7": [0b111, 0b001, 0b010, 0b010, 0b010],
  "8": [0b111, 0b101, 0b111, 0b101, 0b111],
  "9": [0b111, 0b101, 0b111, 0b001, 0b111],
  A: [0b010, 0b101, 0b111, 0b101, 0b101],
  B: [0b110, 0b101, 0b110, 0b101, 0b110],
  C: [0b011, 0b100, 0b100, 0b100, 0b011],
  D: [0b110, 0b101, 0b101, 0b101, 0b110],
  E: [0b111, 0b100, 0b111, 0b100, 0b111],
  F: [0b111, 0b100, 0b111, 0b100, 0b100],
  G: [0b011, 0b100, 0b101, 0b101, 0b011],
  H: [0b101, 0b101, 0b111, 0b101, 0b101],
  J: [0b001, 0b001, 0b001, 0b101, 0b010],
  K: [0b101, 0b101, 0b110, 0b101, 0b101],
  L: [0b100, 0b100, 0b100, 0b100, 0b111],
  M: [0b101, 0b111, 0b111, 0b101, 0b101],
  N: [0b101, 0b111, 0b111, 0b111, 0b101],
  P: [0b110, 0b101, 0b110, 0b100, 0b100],
  Q: [0b010, 0b101, 0b101, 0b111, 0b001],
  R: [0b110, 0b101, 0b110, 0b101, 0b101],
  S: [0b011, 0b100, 0b010, 0b001, 0b110],
  T: [0b111, 0b010, 0b010, 0b010, 0b010],
  U: [0b101, 0b101, 0b101, 0b101, 0b011],
  V: [0b101, 0b101, 0b101, 0b101, 0b010],
  W: [0b101, 0b101, 0b111, 0b111, 0b101],
  X: [0b101, 0b101, 0b010, 0b101, 0b101],
  Y: [0b101, 0b101, 0b010, 0b010, 0b010],
  Z: [0b111, 0b001, 0b010, 0b100, 0b111],
  a: [0b000, 0b011, 0b101, 0b101, 0b011],
  b: [0b100, 0b110, 0b101, 0b101, 0b110],
  c: [0b000, 0b011, 0b100, 0b100, 0b011],
  d: [0b001, 0b011, 0b101, 0b101, 0b011],
  e: [0b000, 0b010, 0b101, 0b110, 0b011],
  f: [0b001, 0b010, 0b111, 0b010, 0b010],
  g: [0b000, 0b011, 0b101, 0b011, 0b001],
  h: [0b100, 0b110, 0b101, 0b101, 0b101],
  i: [0b010, 0b000, 0b010, 0b010, 0b010],
  j: [0b001, 0b000, 0b001, 0b001, 0b110],
  k: [0b100, 0b101, 0b110, 0b101, 0b101],
  m: [0b000, 0b101, 0b111, 0b101, 0b101],
  n: [0b000, 0b110, 0b101, 0b101, 0b101],
  o: [0b000, 0b010, 0b101, 0b101, 0b010],
  p: [0b000, 0b110, 0b101, 0b110, 0b100],
  q: [0b000, 0b011, 0b101, 0b011, 0b001],
  r: [0b000, 0b101, 0b110, 0b100, 0b100],
  s: [0b000, 0b011, 0b010, 0b100, 0b110],
  t: [0b010, 0b111, 0b010, 0b010, 0b001],
  u: [0b000, 0b101, 0b101, 0b101, 0b011],
  v: [0b000, 0b101, 0b101, 0b101, 0b010],
  w: [0b000, 0b101, 0b101, 0b111, 0b101],
  x: [0b000, 0b101, 0b010, 0b010, 0b101],
  y: [0b000, 0b101, 0b101, 0b011, 0b001],
  z: [0b000, 0b111, 0b010, 0b100, 0b111],
  "#": [0b101, 0b111, 0b101, 0b111, 0b101],
  "-": [0b000, 0b000, 0b111, 0b000, 0b000],
  ".": [0b000, 0b000, 0b000, 0b000, 0b010],
  " ": [0b000, 0b000, 0b000, 0b000, 0b000],
  // hex "0" sometimes still appears in block labels
  "0": [0b111, 0b101, 0b101, 0b101, 0b111],
};

const TINY_STEP = TINY_W + 1;

export function measureTinyText(text: string): { w: number; h: number } {
  return { w: Math.max(0, text.length * TINY_STEP - 1), h: TINY_H };
}

export function drawTinyText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  opts?: { align?: "left" | "center"; shadow?: string },
): { x: number; y: number; w: number; h: number } {
  const align = opts?.align ?? "left";
  const { w, h } = measureTinyText(text);
  const left = snap(align === "center" ? x - Math.floor(w / 2) : x);
  const top = snap(y);

  if (opts?.shadow) {
    blitTiny(ctx, text, left + 1, top + 1, opts.shadow);
  }
  blitTiny(ctx, text, left, top, color);
  return { x: left, y: top, w, h };
}

function blitTiny(
  ctx: CanvasRenderingContext2D,
  text: string,
  left: number,
  top: number,
  color: string,
): void {
  ctx.fillStyle = color;
  let cursor = left;
  for (const raw of text) {
    const ch = TINY[raw] ?? TINY[raw.toLowerCase()] ?? TINY[raw.toUpperCase()];
    if (ch) {
      for (let row = 0; row < TINY_H; row++) {
        const bits = ch[row]!;
        for (let col = 0; col < TINY_W; col++) {
          if (bits & (1 << (TINY_W - 1 - col))) {
            ctx.fillRect(cursor + col, top + row, 1, 1);
          }
        }
      }
    }
    cursor += TINY_STEP;
  }
}

/** Explorer deep-link for a transaction (base58 id). */
export function explorerTxUrl(txid: string, network = "testnet"): string {
  const id = encodeURIComponent(toBase58Id(txid));
  // Explorer routes: /tx/:id (mainnet) and /testnet/tx/:id
  return network === "testnet"
    ? `https://explorer.arch.network/testnet/tx/${id}`
    : `https://explorer.arch.network/tx/${id}`;
}

/** Explorer deep-link for a block height. */
export function explorerBlockUrl(height: number, network = "testnet"): string {
  return network === "testnet"
    ? `https://explorer.arch.network/testnet/blocks/${height}`
    : `https://explorer.arch.network/blocks/${height}`;
}
